import Button from '@pelckmans/elementis/components/button';
import Heading from '@pelckmans/elementis/components/heading';
import Link from '@pelckmans/elementis/components/link';
import Panel from '@pelckmans/elementis/components/panel';
import Paragraph from '@pelckmans/elementis/components/paragraph';
import Leerpoort from '@pelckmans/elementis/icons/leerpoort';
import Pelckmans from '@pelckmans/elementis/icons/pelckmans';
import Smartschool from '@pelckmans/elementis/icons/smartschool';
import User from '@pelckmans/elementis/icons/user';
import { Fragment, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../api';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useRoutes } from '../../hooks/useRoutes';
import useStudioGroup from '../../hooks/useStudioGroup';
import ContentBody from '../../layout/content/ContentBody';
import ContentFooter from '../../layout/content/ContentFooter';
import Footer from '../../layout/footer/Footer';
import CookieAlert from '../../shared-components/CookieAlert';
import Separator from './components/Separator';
import Tile from './components/Tile';
import Teacher from './components/icons/Teacher';
import Student from './components/icons/Student';

const oauthUrl = `${API_BASE_URL}/oauth/authorize`;
const BASE = 'PAGES.LOGIN';

const loginProviders = {
  SO: ['pelckmans', 'smartschool'],
  ES: ['pelckmans', 'smartschool'],
  BAO: ['leerpoort-leerkracht', 'leerpoort'],
  VO: ['zogezegd-leerkracht', 'zogezegd'],
} as const;

type LoginButtonProps = {
  params: URLSearchParams;
  children: React.ReactNode;
};

const LoginButtons = {
  pelckmans: ({ params, children }: LoginButtonProps) => (
    <Button
      size="lg"
      startIcon={<Pelckmans />}
      as="link"
      prominence="primary"
      href={`${oauthUrl}?provider=pelckmans&${params}`}
      className="option-button"
    >
      {children}
    </Button>
  ),
  smartschool: ({ params, children }: LoginButtonProps) => (
    <Button
      size="lg"
      startIcon={<Smartschool />}
      as="link"
      prominence="neutral"
      href={`${oauthUrl}?provider=smartschool&prompt=login&${params}`}
      className="option-button"
    >
      {children}
    </Button>
  ),
  'leerpoort-leerkracht': ({ params, children }: LoginButtonProps) => (
    <Button
      size="lg"
      startIcon={<Leerpoort />}
      as="link"
      prominence="primary"
      href={`${oauthUrl}?provider=leerpoort-leerkracht&${params}`}
      className="option-button"
    >
      {children}
    </Button>
  ),
  leerpoort: ({ params, children }: LoginButtonProps) => (
    <Button
      size="lg"
      startIcon={<User />}
      as="link"
      prominence="secondary"
      href={`${oauthUrl}?provider=leerpoort&${params}`}
      className="option-button"
    >
      {children}
    </Button>
  ),
  'zogezegd-leerkracht': ({ params, children }: LoginButtonProps) => (
    <Button
      size="lg"
      startIcon={<Pelckmans />}
      as="link"
      prominence="primary"
      href={`${oauthUrl}?provider=zogezegd-leerkracht&${params}`}
      className="option-button"
    >
      {children}
    </Button>
  ),
  zogezegd: ({ params, children }: LoginButtonProps) => (
    <Button
      size="lg"
      startIcon={<User />}
      as="link"
      prominence="secondary"
      href={`${oauthUrl}?provider=zogezegd&${params}`}
      className="option-button"
    >
      {children}
    </Button>
  ),
};

const baoTiles = {
  'leerpoort-leerkracht': { variant: 'primary', icon: <Teacher /> },
  leerpoort: { variant: 'secondary', icon: <Student /> },
} as const;

const infoLinks: Record<string, string> = {
  ES: 'https://info.pelckmansportail.be/',
  BAO: 'https://www.pelckmans.be/onderwijsniveaus/lager-onderwijs.html',
  VO: 'https://www.pelckmans.be/onderwijsniveaus/volwassenenonderwijs.html',
  SO: 'https://info.pelckmansportaal.be/wat-is-pelckmans-portaal/',
};

const helpLinks: Record<string, string> = {
  SO: 'https://info.pelckmansportaal.be/aanmelden/',
  ES: 'https://info.pelckmansportail.be/foire-aux-questions/#connecter',
  VO: 'https://zogezegd.pelckmans.be/info-hulp',
};

export default function Login() {
  const [search] = useSearchParams();
  const returnUrl = search.get('returnUrl');
  const provider = search.get('provider');
  const platform = search.get('platform');
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const studioGroup = useStudioGroup();
  const { buildAbsolutePath } = useRoutes();
  const location = useLocation();
  const shouldAutoLoginWithSmartschool = provider === 'smartschool' && ['SO', 'ES'].includes(studioGroup);

  const params = useMemo(() => {
    const oauthParams = new URLSearchParams();
    if (returnUrl) {
      oauthParams.set('returnUrl', returnUrl);
    }

    oauthParams.set('language', language);
    oauthParams.set('redirect_uri', `${window.location.origin}${buildAbsolutePath(['LOGIN_CALLBACK'])}`);
    oauthParams.set('register_return_url', window.location.origin);
    oauthParams.set('studioGroup', studioGroup);

    return oauthParams;
  }, [returnUrl, language, buildAbsolutePath, studioGroup]);

  const smartschoolAuthorizeUrl = useMemo(() => {
    const authorizeParams = new URLSearchParams(params);
    authorizeParams.set('provider', 'smartschool');
    authorizeParams.set('prompt', 'login');
    if (platform !== null) authorizeParams.set('platform', platform);

    return `${oauthUrl}?${authorizeParams.toString()}`;
  }, [params, platform]);

  useEffect(() => {
    if (shouldAutoLoginWithSmartschool) {
      window.location.replace(smartschoolAuthorizeUrl);
    }
  }, [shouldAutoLoginWithSmartschool, smartschoolAuthorizeUrl]);

  useDocumentTitle(t('PAGES.LOGIN.DOCUMENT_TITLE'));

  if (shouldAutoLoginWithSmartschool) return null;

  if (studioGroup === 'VO' && location.pathname !== buildAbsolutePath(['LOGIN_VO_TEACHER'])) {
    window.location.replace(oauthUrl + `?${params.toString()}&provider=zogezegd`);
    return null;
  }

  return (
    <div className="page login-page">
      <div className="page-content">
        <div className="main-layout">
          <div className="main-layout-container">
            <div className="content-layout">
              <main>
                <div className="margin-wrapper">
                  <div className="content-header">
                    <div className="max-container">
                      <CookieAlert />
                    </div>
                  </div>
                  <ContentBody>
                    <Panel
                      headerText={t(`${BASE}.HEADER.TEXT`)}
                      headerSubText={t(`${BASE}.HEADER.SUB_TEXT`)}
                      logoProps={{
                        'aria-label': t(`${BASE}.LOGO.A11Y_LABEL`),
                        size: ['SO', 'ES'].includes(studioGroup) ? 'md' : 'sm',
                        variant: studioGroup,
                      }}
                      footer={
                        <>
                          <Link to={infoLinks[studioGroup]} target="_blank" rel="noopener noreferrer">
                            {t(`${BASE}.FOOTER.EXPLANATION`)}
                          </Link>
                          <Link
                            {...(studioGroup === 'BAO'
                              ? { to: buildAbsolutePath(['HELP']) }
                              : { to: helpLinks[studioGroup], target: '_blank', rel: 'noopener noreferrer' })}
                          >
                            {t(`${BASE}.FOOTER.HELP`)}
                          </Link>
                        </>
                      }
                    >
                      <div className="login-content">
                        <div className="text">
                          <Heading level={2} removeSpacing="all" className="heading-value">
                            {t(`${BASE}.BODY.TITLE`)}
                          </Heading>
                          <Paragraph removeSpacing className="body-value">
                            {t(`${BASE}.BODY.SUB_TEXT`)}
                          </Paragraph>
                        </div>
                        <div className="actions">
                          <div className="actions-options">
                            {loginProviders[studioGroup as keyof typeof loginProviders].map((provider, index) => {
                              const LoginButton = LoginButtons[provider as keyof typeof LoginButtons];
                              const showSeparator = index === 0;

                              return (
                                <Fragment key={provider}>
                                  <div className="option">
                                    {studioGroup === 'BAO' ? (
                                      <Tile
                                        href={`${oauthUrl}?provider=${provider}&${params}`}
                                        variant={baoTiles[provider as keyof typeof baoTiles].variant}
                                        icon={baoTiles[provider as keyof typeof baoTiles].icon}
                                      >
                                        {t(`${BASE}.ACTIONS.LOGIN_${provider.toUpperCase().replace(/-/g, '_')}.TITLE`)}
                                      </Tile>
                                    ) : (
                                      <>
                                        <LoginButton params={params}>
                                          {t(
                                            `${BASE}.ACTIONS.LOGIN_${provider.toUpperCase().replace(/-/g, '_')}.TITLE`,
                                          )}
                                        </LoginButton>
                                        {studioGroup === 'VO' && (
                                          <Paragraph
                                            removeSpacing
                                            className="option-explanation"
                                            data-testid={`${provider}-explanation`}
                                          >
                                            {t(
                                              `${BASE}.ACTIONS.LOGIN_${provider
                                                .toUpperCase()
                                                .replace(/-/g, '_')}.EXPLANATION`,
                                            )}
                                          </Paragraph>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  {showSeparator && <Separator />}
                                </Fragment>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </Panel>
                  </ContentBody>
                  <ContentFooter>
                    <Footer />
                  </ContentFooter>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
